import React, { useCallback, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone';
import config from './../app/fileUploader.json';
import { FiAlertCircle, FiCheckCircle, FiFileText, FiInfo, FiLoader, FiTrash2, FiUploadCloud } from 'react-icons/fi';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import { FaRegFilePdf } from 'react-icons/fa';
import { uploadEvidence } from '../app/store/evidences/thunk';

export const Uploader = ({reference,userRegister}) => {

    const [queueFiles, setQueueFiles] = useState([]);
    const [loading, setLoading] = useState(false);

    const dispatch = useDispatch();

    const onDrop = useCallback((acceptedFiles) => {
        setQueueFiles(prev => {
            const newFiles = [];
            const duplicated = [];

            acceptedFiles.forEach(file => {
                const exists = prev.some(f =>
                    f.file.name === file.name &&
                    f.file.size === file.size
                );

                if (exists) {
                    duplicated.push(file.name);
                } else {
                    newFiles.push({
                        id: crypto.randomUUID(),
                        file,
                        preview: URL.createObjectURL(file),
                        status: 'pending',
                        error: null
                    });
                }
            });

            // ⚠️ Mostrar alerta AQUÍ es seguro
            if (duplicated.length > 0) {
                Swal.fire({
                    title: "Atención",
                    html: `
                    <p>Estos archivos ya estaban adjuntos:</p>
                    <ul class="text-start">
                        ${duplicated.map(f => `<li>${f}</li>`).join("")}
                    </ul>
                `,
                    icon: "warning",
                    confirmButtonText: "Aceptar",
                    buttonsStyling: false,
                    customClass: {
                        confirmButton: "btn btn-primary"
                    }
                });
            }

            return [...prev, ...newFiles];
        });
    }, []);

    const { getRootProps, getInputProps, acceptedFiles, fileRejections, isDragActive } = useDropzone(
        {
            onDrop,
            accept: config.rules.allowed_types,
            maxSize: config.rules.max_size_mb * 1024 * 1024,
            multiple: config.rules.multiple,
            maxFiles: config.rules.max_files
        });

    useEffect(() => {
        return () => {
            queueFiles.forEach(f => URL.revokeObjectURL(f.preview));
        };
    }, [queueFiles]);

    const removeFromQueue = (id) => {
        setQueueFiles(files => files.filter(f => f.id !== id));
    };

    const confirmUpload = () => {
        Swal.fire({
            icon: "question",
            title: "Atencion",
            text: "Esta por subir archivos como evidencia, ¿Desea de continuar con esta acción?",
            customClass: {
                confirmButton: 'btn btn-primary  me-2',
                cancelButton: 'btn btn-secondary'
            },
            showCancelButton: true, // <-- ¡Este es importante!
            confirmButtonText: 'Sí, continuar',
            cancelButtonText: 'No, cancelar',
            buttonsStyling: false // Esto permite que tus clases personalizadas funcionen bien
        }).then((result) => {
            if (result.isConfirmed) {
                uploadFiles();
            }
        })
    }

    const uploadFiles = async () => {

        if (loading) return;
        setLoading(true);

        for (const f of queueFiles) {
            if (f.status === 'success') continue;

            updateStatus(f.id, 'uploading');

            const res = await dispatch(uploadEvidence(reference,userRegister,f, {
                module: config.module,
                entity_type: config.entity_type,
                entity_id: 1,
            }));

            if (res.ok) {
                updateStatus(f.id, 'success');
            } else {
                updateStatus(f.id, 'error', res.error);
            }
        }

        setLoading(false);
    };

    const updateStatus = (id, status, error = null) => {
        setQueueFiles(files =>
            files.map(f =>
                f.id === id
                    ? { ...f, status, error }
                    : f
            )
        );
    };

    const retryFailed = () => {
        setQueueFiles(files =>
            files.map(f =>
                f.status === 'error'
                    ? { ...f, status: 'pending', error: null }
                    : f
            )
        );
    };

    const openFile = (file) => {
        const url = URL.createObjectURL(file);
        window.open(url, '_blank');

        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    const hasFailedFiles = queueFiles.some(f => f.status === 'error');

    return (
        <div className='card'>

            <div className="card-header border-bottom">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
                    <h4 className="fw-bold mb-0">Evidencias:</h4>
                    <div className="d-flex gap-1">
                        <div className="btn-group" role="group" aria-label="Basic example">
                            <button className="btn btn-outline-primary  me-2" onClick={confirmUpload} disabled={loading || queueFiles.length === 0}> <FiUploadCloud size={22} /> {loading ? 'Subiendo...' : ''} </button>
                            <button className="btn btn-outline-danger  me-2" onClick={() => setQueueFiles([])} disabled={loading || queueFiles.length === 0}> <FiTrash2 size={22} />   </button>
                            {hasFailedFiles && (
                                <button className="btn btn-outline-warning " onClick={retryFailed} disabled={loading}>
                                    <FiUploadCloud size={22} />
                                    Reintentar ({queueFiles.filter(f => f.status === 'error').length})
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className='card-body'>
                <div {...getRootProps()} className={`border rounded p-4 text-center mb-3 ${isDragActive ? 'border-primary bg-light' : ''}`} style={{ cursor: 'pointer' }}            >
                    <input {...getInputProps()} />
                    <h5 className="mb-0 fw-bold">
                        {isDragActive ? 'Suelta los archivos aquí...' : 'Arrastra archivos o haz clic para seleccionar'}
                    </h5>
                </div>

                {/* ERRORES */}
                {fileRejections.length > 0 && (
                    <div className="alert alert-danger">
                        {fileRejections.map(({ file, errors }) => (
                            <div key={file.name}>
                                <strong>{file.name}</strong>
                                <ul className="mb-0">
                                    {errors.map(e => (
                                        <li key={e.code}>{e.message}</li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                )}

                <div className='mt-1' style={{ maxHeight: "190px", overflowY: "auto", overflowX: "hidden", }}>
                    <div className="row" >

                        {queueFiles.length === 0 && (
                            <p className="text-start fw-bold text-muted mt-3">
                                <FiInfo size={22} /> No hay archivos adjuntados
                            </p>
                        )}

                        {queueFiles.map((f) => (
                            <div key={f.id} className="col-12 col-sm-6 col-md-6 col-lg-6" >
                                <div className="" style={{ cursor: "pointer" }} onClick={() => openFile(f.file)} >
                                    <div className="card-body">
                                        <div className="row align-items-center">
                                            {/* Preview */}
                                            <div className="col-8">
                                                {f.file.type.startsWith("image/") ? (
                                                    <img src={f.preview} alt="preview" className="rounded" style={{ width: 40, height: 40, objectFit: "cover", }} />
                                                ) : f.file.type === "application/pdf" ? (

                                                    <FaRegFilePdf size={40} className="text-danger" />
                                                ) : (
                                                    <FiFileText size={40} className="text-dark" />
                                                )}
                                            </div>
                                            <div className="col-2">
                                                <button className="btn btn-sm btn-outline-danger" onClick={(e) => { e.stopPropagation(); removeFromQueue(f.id); }} >
                                                    <FiTrash2 size={22} />
                                                </button>
                                            </div>

                                            {/* Info */}
                                            <div className="col-12">
                                                <strong className="d-block text-truncate" title={f.file.name}  > {f.file.name}</strong>
                                                <div className="text-muted small">
                                                    {(f.file.size / 1024 / 1024).toFixed(2)}{" "}MB
                                                </div>
                                            </div>

                                            {/* Delete */}
                                            <div className="mt-2">
                                                {f.status === 'pending' && (
                                                    <span className="badge bg-dark">Pendiente</span>
                                                )}

                                                {f.status === 'uploading' && (
                                                    <span className="badge bg-info d-inline-flex align-items-center gap-1">
                                                        <FiLoader className="spinner-border spinner-border-sm" />
                                                        Subiendo
                                                    </span>
                                                )}

                                                {f.status === 'success' && (
                                                    <span className="badge bg-success d-inline-flex align-items-center gap-1">
                                                        <FiCheckCircle size={18} />
                                                        Exito, subido
                                                    </span>
                                                )}

                                                {f.status === 'error' && (
                                                    <span className="badge bg-danger d-inline-flex align-items-center gap-1">
                                                        <FiAlertCircle />
                                                        Error... reintentar
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

            </div>
        </div>
    )
}
